import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {
  CosechaViewModel,
  EntregaViewModel,
  MercadoViewModel,
  MonedaViewModel,
  NegocioTipoViewModel, PosicionViewModel,
  ProductoViewModel, ResponseGeneric
} from '@utils/ViewModels';
import {FormSelectItem} from '@common/form-components';
import * as moment from 'moment';
import {HttpClient} from '@services/http-client';
import { MdSnackBar } from '@angular/material';
import { Response } from '@angular/http';
import {Observable} from 'rxjs/Observable';

export const NEG_TYPE_DISPO = 'Disponible';
export const NEG_TYPE_FORWARD = 'Forward';
export const NEG_TYPE_PAG_DIFE = 'Pago Diferido';

@Injectable()
// En este servicio vamos a guardar informacion comun que es usada en muchas partes del sistema
export class NegocioDataService {

  private productosBS = new BehaviorSubject<ProductoViewModel[]>([]);
  public $productosOptions = this.productosBS.asObservable();
  // devuelve los valores de producto en formato FormSelectItem para ser usados en un form
  public $productosOptionsAsForm = this.productosBS.asObservable()
    .map(arr => {
      return arr.map(prod => ({Id: prod.Id, Label: prod.Producto, RawValue: JSON.stringify(prod)}) as FormSelectItem)
    });

  private mercadosBS = new BehaviorSubject<MercadoViewModel[]>([]);
  // devuleve los mercados filtrados
  public $mercadosOptions = this.mercadosBS.asObservable().map(x => x.filter(merc => merc.Visibilidad));
  // devuelve los valores de mercado en formato FormSelectItem para ser usados en un form
  public $mercadosOptionsAsForm = this.mercadosBS.asObservable()
    .map(arr => {
      return arr
        .filter(merc => merc.Visibilidad)
        .map(merc => ({Id: merc.Id, Label: merc.Mercado, RawValue: JSON.stringify(merc)}) as FormSelectItem)
    });
  // devuelve los valores de mercado sin filtrar por visibilidad (admin only)
  public $mercadosAllOptions = this.mercadosBS.asObservable();

  private cosechaBS = new BehaviorSubject<CosechaViewModel[]>([]);
  private $baseCosechaOptions = this.cosechaBS.map(arr => arr.sort((a, b) => { return b.AnoDesde - a.AnoDesde }));

  public $cosechaOptions = this.$baseCosechaOptions;
  public $cosechaOptionsAsForm = this.$baseCosechaOptions
    .map(arr => arr.map(cos => ({Id: cos.Id, Label: cos.Cosecha, RawValue: JSON.stringify(cos)}) as FormSelectItem));

  private monedaBS = new BehaviorSubject<MonedaViewModel[]>([]);
  public $monedaOptions = this.monedaBS.asObservable();
  public $monedaOptionsAsForm = this.monedaBS.asObservable()
    .map(arr => arr.map(mon => ({Id: mon.Id, Label: mon.Moneda + ' ' + mon.Simbolo, RawValue: JSON.stringify(mon)}) as FormSelectItem));
  public $monedaOptionsWithPesificarAsForm = this.monedaBS.asObservable()
    .map(arr => arr.map(mon => ({Id: mon.Id, Label: mon.Moneda + ' ' + mon.Simbolo, RawValue: JSON.stringify(mon)}) as FormSelectItem))
    .map(arr => {
      arr.push({Id: -1, Label: 'Dólares a pesificar u$p'});
      return arr;
    });

  private negTipoBS = new BehaviorSubject<NegocioTipoViewModel[]>([])
  public $negocioTipo = this.negTipoBS.asObservable();
  public $negocioTipoNewNegocio = this.negTipoBS.asObservable()
    // filtramos los tipos de negocios a solo orden, intencion y olvidado (1, 2 y 6 segun Id)
    .map(x => x.filter(y => [1, 2, 6].indexOf(y.Id) > -1))
    .map(x => x.map(tipo => ({Id: tipo.Id, Label: tipo.Tipo, RawValue: JSON.stringify(tipo)}) as FormSelectItem));


  // hardcoded dado que es un dato de paso, no un dato que se registre en DB
  public $negEntregaTipo = new BehaviorSubject<FormSelectItem[]>([
    {Id: NEG_TYPE_DISPO, Label: NEG_TYPE_DISPO},
    {Id: NEG_TYPE_FORWARD, Label: NEG_TYPE_FORWARD},
    {Id: NEG_TYPE_PAG_DIFE, Label: NEG_TYPE_PAG_DIFE},
  ]).asObservable();

  private entregaBS = new BehaviorSubject<EntregaViewModel[]>([]);
  public $entrega = this.entregaBS.asObservable();
  public $entregaDispo = this.entregaBS.asObservable().map(x => x.filter(y => !y.EsPosicion));

  private posicionBS = new BehaviorSubject<PosicionViewModel[]>([]);
  public $posicionAll = this.posicionBS.asObservable()
    .map(x => x.sort((a, b) => moment(a.Fecha).unix() - moment(b.Fecha).unix()));

  constructor(
    private apiService: HttpClient,
    private snackBar: MdSnackBar,
  ) {
    this.inicializarCommonData();
  }

  inicializarCommonData() {
    if (!localStorage.getItem('userId')) {
      console.warn('No se encontró un userId para actualizar CommonDataService')
      return;
    }
    this.updateProductos();
    this.updateMercados();
    this.updateCosecha();
    this.updateMoneda();
    this.updateNegocioTipo();
    this.updateEntrega();
    this.updatePosicion();
  }

  // Actualiza los valores de productos del servicio
  private updateProductos(): void {
    this.apiService.get('/api/producto').take(1)
      .map((res: Response) => {
        const ret = res.json() as ResponseGeneric<ProductoViewModel[]>;
        return ret.ResponseData;
      })
      .subscribe({
        next: x => {
          this.productosBS.next(x);
        },
        error: () => this.sendErrorMessage('Productos')
      });
  }

  private updateMercados(): void {
    this.apiService.get('/api/precio/cotizacionBCR/get-mercados').take(1)
      .map((res: Response) => {
        const ret = res.json() as ResponseGeneric<MercadoViewModel[]>;
        return ret.ResponseData;
      })
      .subscribe({
        next: x => {
          this.mercadosBS.next(x);
        },
        error: () => this.sendErrorMessage('Mercados')
      })
  }

  private updateCosecha(): void {
    this.apiService.get('/api/cosecha').take(1)
      .map((res: Response) => {
        const ret = res.json() as ResponseGeneric<CosechaViewModel[]>;
        return ret.ResponseData;
      })
      .subscribe({
        next: x => {
          this.cosechaBS.next(x);
        },
        error: () => this.sendErrorMessage('Cosechas')
      })
  }

  private updateMoneda(): void {
    this.apiService.get('/api/moneda').take(1)
      .map((res: Response) => {
        const ret = res.json() as ResponseGeneric<MonedaViewModel[]>;
        return ret.ResponseData;
      })
      .subscribe({
        next: x => {
          this.monedaBS.next(x);
        },
        error: () => this.sendErrorMessage('Monedas')
      })
  }

  private updateNegocioTipo(): void {
    this.apiService.get('/api/negocio/tipo').take(1)
      .map((res: Response) => {
        const ret = res.json() as ResponseGeneric<NegocioTipoViewModel[]>;
        return ret.ResponseData;
      })
      .subscribe({
        next: x => this.negTipoBS.next(x),
        error: () => this.sendErrorMessage('Tipos de Negocio')
      });
  }

  private updateEntrega(): void {
    this.apiService.get('/api/entrega').take(1)
      .map((res: Response) => {
        const ret = res.json() as ResponseGeneric<EntregaViewModel[]>;
        return ret.ResponseData;
      })
      .subscribe({
        next: x => this.entregaBS.next(x),
        error: () => this.sendErrorMessage('Entrega')
      });
  }

  public updatePosicion(): void {
    this.apiService.get('/api/posicion').take(1)
      .map((res: Response) => {
        const ret = res.json() as ResponseGeneric<PosicionViewModel[]>;
        return ret.ResponseData;
      })
      .subscribe({
        next: x => this.posicionBS.next(x),
        error: () => this.sendErrorMessage('Entrega')
      });
  }

  private sendErrorMessage(item: string): void {
    this.snackBar.open(
      `Ha ocurrido un error al intentar obtener: ${item}`,
      'Aceptar',
      {duration: 5000}
    );
  }

}

/**
 * Devuelve la fecha en formato string en formato de mes de 3 letras y año. Ej: 12/12/2012 -> DIC-12
 * @param date Fecha en string, debe ser reconocible por moment en su constructor sin especificar formato
 * */
export function dateToMonthAndYearFormat(date: string) {
  moment.locale('es');
  return `${moment(date).format('MMM')}-${moment(date).format('YY')}`.replace('.', '').toUpperCase()
}




// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/services/negocio-data.service.ts